import React, {useEffect, useState} from 'react'
import {PageContainer} from '../../assets/styles/layout'
import {borderRadiusMedium} from '../../assets/styles/border'
import {makeStyles} from '@mui/styles'
import * as Sentry from '@sentry/react'
import {Spacing, Colors} from '../../assets/styles'
import PageHeader from '../PageHeader'
import MetricDataAPI from '../../lib/MetricData/MetricDataAPI'
import {useSelector} from 'react-redux'
import LoadingStateIconAndMessage from '../LoadingStateIconAndMessage'
import DataComingSoon from '../DataComingSoon'
import MessageBox from '../MessageBox'
import RXRIcon from '../RXRIcon'
import RXRButton from '../RXRButton'
import NPSScoreCard from '../ResidentSentiment/NPSScoreCard'
import ToggleButtons from '../ToggleButtons'
import ExitSurveySummaryCards from './ExitSurveySummaryCards'
import ReasonForLeavingChart from './ReasonForLeavingChart'
import RecentVideoResponses from './RecentVideoResponses'
import ExitSurveyNPSTrendChart from './ExitSurveyNPSTrendChart'
import SubmissionDetails from './SubmissionDetails'

const TIMEFRAME_30_DAYS = 0
const WINDOW_LABELS = ['30 day', '6 month']

function ExitSurveys() {
  const classes = useStyles()

  const [isLoading, setIsLoading] = useState(false)
  const [hasDataError, setHasDataError] = useState(false)
  const [dashboard, setDashboard] = useState(null)
  const [npsTimeframe, setNpsTimeframe] = useState(TIMEFRAME_30_DAYS)
  const [reloadCount, setReloadCount] = useState(0)
  const activeBuildingId = useSelector(state => state.Buildings.activeBuildingId)
  const buildingsLookup = useSelector(state => state.Buildings.buildingsLookup)
  const activeBuildingYardiId = buildingsLookup[activeBuildingId]?.yardiId

  useEffect(() => {
    if (!activeBuildingYardiId) {
      setDashboard(null)
      return undefined
    }
    let cancelled = false
    setIsLoading(true)
    setHasDataError(false)
    MetricDataAPI.getExitSurveyDashboard(activeBuildingYardiId)
      .then(payload => {
        if (!cancelled) {
          setDashboard(payload)
        }
      })
      .catch(err => {
        console.error(err)
        Sentry.withScope(scope => {
          scope.setTag('ExitSurvey', 'getExitSurveyDashboard')
          scope.setExtra('activeBuildingYardiId', activeBuildingYardiId)
          Sentry.captureException(err)
        })
        if (!cancelled) {
          setHasDataError(true)
          setDashboard(null)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [activeBuildingYardiId, reloadCount])

  const is30Days = npsTimeframe === TIMEFRAME_30_DAYS
  // both payload sources (the live mapping and the mock fallback) always carry both windows with an
  // nps object — absent metrics arrive as NaN inside it, which the card renders as N/A
  const npsWindow = dashboard && (is30Days ? dashboard.thirtyDay : dashboard.sixMonth)

  return (
    <div className={classes.exitSurveysPage}>
      <PageHeader title={'Exit Surveys'} className={classes.titleContainer} />
      {isLoading ? (
        <div className={classes.innerPage}>
          <LoadingStateIconAndMessage message={'Loading Exit Survey data'} />
        </div>
      ) : hasDataError ? (
        <div className={classes.innerPage}>
          <MessageBox
            className={classes.errorMessage}
            retry={() => setReloadCount(count => count + 1)}
            buttonType={RXRButton.TYPE_DESTRUCTIVE}
            color={Colors.rxrRedColor}
            icon={RXRIcon.ERROR}
          >
            Failed to load exit survey data.
          </MessageBox>
        </div>
      ) : !dashboard ? (
        <div className={classes.emptyStateCard}>
          <DataComingSoon iconSize={50} />
        </div>
      ) : (
        <div className={classes.innerPage}>
          <ExitSurveySummaryCards summaryByWindow={{thirtyDay: dashboard.thirtyDay?.summary, sixMonth: dashboard.sixMonth?.summary}} />

          <ReasonForLeavingChart
            breakdownByWindow={{thirtyDay: dashboard.thirtyDay?.reasonBreakdown, sixMonth: dashboard.sixMonth?.reasonBreakdown}}
          />
          <RecentVideoResponses responses={dashboard.videoResponses || []} />
          <SubmissionDetails submissions={dashboard.submissions || []} />

          <NPSScoreCard
            cardTitle={'Exit Survey NPS score'}
            titleControl={
              <ToggleButtons
                className={classes.switcher}
                buttonLabels={WINDOW_LABELS}
                selected={npsTimeframe}
                onChange={setNpsTimeframe}
                inverseColors
              />
            }
            npsResponses={npsWindow.nps.responses}
            npsScore={npsWindow.nps.score}
            npsScoreChange={npsWindow.nps.scoreChange}
            npsScorePercentagePromoters={npsWindow.nps.promoters}
            npsScorePercentagePromotersChangeFromLastMonth={npsWindow.nps.promotersChange}
            npsScorePercentagePassives={npsWindow.nps.passives}
            npsScorePercentagePassivesChangeFromLastMonth={npsWindow.nps.passivesChange}
            npsScorePercentageDetractors={npsWindow.nps.detractors}
            npsScorePercentageDetractorsChangeFromLastMonth={npsWindow.nps.detractorsChange}
          />
          <ExitSurveyNPSTrendChart trend={dashboard.npsTrend} />
        </div>
      )}
    </div>
  )
}

const useStyles = makeStyles({
  exitSurveysPage: {
    ...PageContainer,
    height: 'auto',
    backgroundColor: Colors.rxrSidePanelBackgroundCoor,
  },
  titleContainer: {
    borderBottom: 'none',
    backgroundColor: 'transparent',
  },
  innerPage: {
    padding: Spacing.spaceMedium,
    paddingTop: 0,
    height: '100%',
  },
  errorMessage: {
    marginBottom: Spacing.spaceMedium,
  },
  emptyStateCard: {
    marginTop: 80
  },
  switcher: {
    flexShrink: 0,
    width: 160,
  },
})

export default ExitSurveys
